// FILE: client/src/actions/contactActions.js

import * as C from "../constants/contactConstants";
import api from "../utils/axiosInstance";
import { getApiErrorMessage } from "../utils/errorMessages";

const getErrorMessage = (error, fallback) =>
  getApiErrorMessage(error, { fallback });

const normalizeString = (value = "") => String(value || "").trim();

const encodePath = (value = "") => encodeURIComponent(normalizeString(value));

function buildClientError(message, code = "BAD_REQUEST", status = 400) {
  const error = new Error(message);
  error.code = code;
  error.status = status;
  return error;
}

/**
 * Create contact message
 * POST /api/contact
 */
export const createContactMessage =
  (name, email, subject, message) => async (dispatch) => {
    try {
      dispatch({ type: C.CONTACT_CREATE_REQUEST });

      const payload = {
        name: normalizeString(name),
        email: normalizeString(email),
        subject: normalizeString(subject),
        message: normalizeString(message),
      };

      const { data } = await api.post("/contact", payload);

      dispatch({
        type: C.CONTACT_CREATE_SUCCESS,
        payload: data,
      });

      return data;
    } catch (error) {
      const payload = getErrorMessage(
        error,
        "We could not send your message right now. Please try again later.",
      );

      dispatch({
        type: C.CONTACT_CREATE_FAIL,
        payload,
      });

      throw error;
    }
  };

/**
 * List contact messages, admin
 * GET /api/contact/admin
 */
export const listContactMessages = () => async (dispatch) => {
  try {
    dispatch({ type: C.CONTACT_LIST_REQUEST });

    const { data } = await api.get("/contact/admin");

    dispatch({
      type: C.CONTACT_LIST_SUCCESS,
      payload: data,
    });

    return data;
  } catch (error) {
    const payload = getErrorMessage(
      error,
      "We could not load contact messages right now. Please try again later.",
    );

    dispatch({
      type: C.CONTACT_LIST_FAIL,
      payload,
    });

    throw error;
  }
};

/**
 * Get single contact message, admin
 * GET /api/contact/admin/:id
 */
export const getContactMessageById = (id) => async (dispatch) => {
  const safeId = normalizeString(id);

  try {
    dispatch({
      type: C.CONTACT_DETAILS_REQUEST,
      meta: { id: safeId },
    });

    if (!safeId) {
      throw buildClientError(
        "Contact message id is required.",
        "CONTACT_ID_REQUIRED",
      );
    }

    const { data } = await api.get(`/contact/admin/${encodePath(safeId)}`);

    dispatch({
      type: C.CONTACT_DETAILS_SUCCESS,
      payload: data,
      meta: { id: safeId },
    });

    return data;
  } catch (error) {
    const payload = getErrorMessage(
      error,
      "We could not load this contact message right now. Please try again later.",
    );

    dispatch({
      type: C.CONTACT_DETAILS_FAIL,
      payload,
      meta: { id: safeId },
    });

    throw error;
  }
};

/**
 * Delete contact message, admin
 * DELETE /api/contact/admin/:id
 */
export const deleteContactMessage = (id) => async (dispatch) => {
  const safeId = normalizeString(id);

  try {
    dispatch({
      type: C.CONTACT_DELETE_REQUEST,
      meta: { id: safeId },
    });

    if (!safeId) {
      throw buildClientError(
        "Contact message id is required.",
        "CONTACT_ID_REQUIRED",
      );
    }

    await api.delete(`/contact/admin/${encodePath(safeId)}`);

    dispatch({
      type: C.CONTACT_DELETE_SUCCESS,
      payload: { id: safeId },
      meta: { id: safeId },
    });

    return { id: safeId };
  } catch (error) {
    const payload = getErrorMessage(
      error,
      "We could not delete this contact message right now. Please try again later.",
    );

    dispatch({
      type: C.CONTACT_DELETE_FAIL,
      payload,
      meta: { id: safeId },
    });

    throw error;
  }
};

/**
 * Update contact message status, admin
 * PATCH /api/contact/admin/:id
 */
export const updateContactMessageStatus = (id, status) => async (dispatch) => {
  const safeId = normalizeString(id);
  const safeStatus = normalizeString(status).toLowerCase();

  try {
    dispatch({
      type: C.CONTACT_UPDATE_STATUS_REQUEST,
      meta: { id: safeId, status: safeStatus },
    });

    if (!safeId) {
      throw buildClientError(
        "Contact message id is required.",
        "CONTACT_ID_REQUIRED",
      );
    }

    if (!safeStatus) {
      throw buildClientError(
        "Contact message status is required.",
        "CONTACT_STATUS_REQUIRED",
      );
    }

    const { data } = await api.patch(`/contact/admin/${encodePath(safeId)}`, {
      status: safeStatus,
    });

    dispatch({
      type: C.CONTACT_UPDATE_STATUS_SUCCESS,
      payload: data,
      meta: { id: safeId, status: safeStatus },
    });

    return data;
  } catch (error) {
    const payload = getErrorMessage(
      error,
      "We could not update this message status right now. Please try again later.",
    );

    dispatch({
      type: C.CONTACT_UPDATE_STATUS_FAIL,
      payload,
      meta: { id: safeId, status: safeStatus },
    });

    throw error;
  }
};

/**
 * Get contact page content, public
 * GET /api/contact/content/page
 */
export const getContactContent = () => async (dispatch) => {
  try {
    dispatch({ type: C.CONTACT_CONTENT_DETAILS_REQUEST });

    const { data } = await api.get("/contact/content/page");

    dispatch({
      type: C.CONTACT_CONTENT_DETAILS_SUCCESS,
      payload: data,
    });

    return data;
  } catch (error) {
    const payload = getErrorMessage(
      error,
      "We could not load contact page content right now. Please try again later.",
    );

    dispatch({
      type: C.CONTACT_CONTENT_DETAILS_FAIL,
      payload,
    });

    throw error;
  }
};

/**
 * Update contact page content, admin
 * PATCH /api/contact/admin/content/page
 */
export const updateContactContent = (contentData) => async (dispatch) => {
  try {
    dispatch({ type: C.CONTACT_CONTENT_UPDATE_REQUEST });

    const { data } = await api.patch(
      "/contact/admin/content/page",
      contentData,
    );

    dispatch({
      type: C.CONTACT_CONTENT_UPDATE_SUCCESS,
      payload: data,
    });

    return data;
  } catch (error) {
    const payload = getErrorMessage(
      error,
      "We could not update contact page content right now. Please try again later.",
    );

    dispatch({
      type: C.CONTACT_CONTENT_UPDATE_FAIL,
      payload,
    });

    throw error;
  }
};
